import { useReportQuery } from '@app/modules/audit/hooks/useReportQuery';
import { auditReports } from '@mi-front/api-clients';
import { useQueryParam } from '@mi-front/core';
import { useStationsGroups } from '../../../../../../../../core/stations/hooks/useStationsGroupsQuery';
import { useStationsWithAudit } from '../../../../../../hooks/useStationsWithAudit';
import { GipReportValues } from '../../config/form/types';
import { mapGipReportDataDtoToValues } from '../../config/utils/gip-report-data-dto-to-values';

/**
 * Reads the report id from the URL query param "report", fetches the report via useReportQuery,
 * and maps report.data.config to form values for pre-filling the GIP preview form.
 * Returns undefined values when no report param is present or when still loading/error.
 */
export function useGipValuesFromReportId(): {
  values: GipReportValues | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const reportId = useQueryParam('report') ?? undefined;
  const stations = useStationsWithAudit();
  const stationGroups = useStationsGroups();

  const query = useReportQuery(reportId);

  if (!reportId) {
    return { values: undefined, isLoading: false, error: null };
  }

  if (query.isLoading) {
    return { values: undefined, isLoading: true, error: null };
  }

  if (query.error) {
    return {
      values: undefined,
      isLoading: false,
      error: query.error as Error,
    };
  }

  const report = query.data as { data?: { config?: auditReports.GipReportDataDto } } | undefined;
  if (!report?.data?.config) {
    return { values: undefined, isLoading: false, error: null };
  }

  const values = mapGipReportDataDtoToValues(report.data.config, stations, stationGroups);

  return { values, isLoading: false, error: null };
}
